import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './DataPage.css';
import { formatDate, formatDateTime } from '../utils/formatDate';

const styles = {
  page: {
    background: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: "'Inter', 'Roboto', 'Open Sans', sans-serif",
  },
  breadcrumb: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '0.25rem',
  },
  breadcrumbLink: {
    color: '#1976d2',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchInput: {
    padding: '0.625rem 1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    width: '300px',
    maxWidth: '100%',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#333',
  },
  btnPrimary: {
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    padding: '0.625rem 1.25rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnDanger: {
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnWarning: {
    background: '#ff9800',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnSecondary: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '0.625rem 1.25rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnEdit: {
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  tableCard: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  tableResponsive: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  th: {
    background: '#fafafa',
    padding: '0.75rem 1rem',
    textAlign: 'left',
    fontWeight: '600',
    color: '#555',
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  td: {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
    verticalAlign: 'middle',
  },
  trEven: {
    background: '#fafafa',
  },
  trOdd: {
    background: '#fff',
  },
  badgeAtivo: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeInativo: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: '#ffebee',
    color: '#c62828',
  },
  actionsCell: {
    display: 'flex',
    gap: '0.4rem',
    flexWrap: 'wrap',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    background: '#fff',
    borderRadius: '8px',
    maxWidth: 'min(500px, 95vw)',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#333',
    margin: 0,
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.4rem',
    color: '#999',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1',
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalFooter: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.35rem',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#666',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    color: '#333',
    outline: 'none',
    background: '#fff',
    boxSizing: 'border-box',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  toggleSwitch: {
    position: 'relative',
    width: '44px',
    height: '24px',
    cursor: 'pointer',
  },
  toggleTrack: (isActive) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '12px',
    background: isActive ? '#4caf50' : '#ccc',
    transition: 'background 0.2s',
  }),
  toggleKnob: (isActive) => ({
    position: 'absolute',
    top: '2px',
    left: isActive ? '22px' : '2px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    transition: 'left 0.2s',
  }),
  alert: (type) => ({
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    fontWeight: '500',
    background: type === 'success' ? '#e8f5e9' : '#ffebee',
    color: type === 'success' ? '#2e7d32' : '#c62828',
    border: `1px solid ${type === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
  }),
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    color: '#999',
    fontSize: '0.95rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#999',
    fontSize: '0.95rem',
  },
  required: {
    color: '#dc3545',
    marginLeft: '2px',
  },
};

function GerenciarUsuarios() {
  const { user } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Feedback
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Modal de criacao/edicao
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    perfil_id: '',
    ativo: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [savingForm, setSavingForm] = useState(false);

  // Modal de reset de senha
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [resetData, setResetData] = useState({ password: '', confirmPassword: '' });
  const [resetErrors, setResetErrors] = useState({});
  const [savingReset, setSavingReset] = useState(false);

  // Setores WhatsApp
  const [setores, setSetores] = useState([]);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [setorModalOpen, setSetorModalOpen] = useState(false);
  const [editingSetor, setEditingSetor] = useState(null);
  const [setorForm, setSetorForm] = useState({ nome: '', descricao: '' });
  const [savingSetor, setSavingSetor] = useState(false);
  const [setorUsuarios, setSetorUsuarios] = useState({}); // { setorId: [users] }
  const [expandedSetor, setExpandedSetor] = useState(null);
  const [addingUserToSetor, setAddingUserToSetor] = useState(null); // setorId when adding
  const [selectedUserForSetor, setSelectedUserForSetor] = useState('');

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/auth/users`);
      setUsuarios(Array.isArray(response.data) ? response.data : response.data.users || []);
    } catch (err) {
      console.error('Erro ao buscar usuarios:', err);
      setFeedback({ type: 'error', message: 'Erro ao carregar a lista de usuarios.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPerfis = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/perfis`);
      setPerfis(Array.isArray(response.data) ? response.data : response.data.perfis || []);
    } catch (err) {
      console.error('Erro ao buscar perfis:', err);
    }
  }, []);

  const fetchSetores = useCallback(async () => {
    setLoadingSetores(true);
    try {
      const res = await axios.get(`${API_URL}/whatsapp/setores`);
      setSetores(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erro ao buscar setores:', err);
    } finally {
      setLoadingSetores(false);
    }
  }, []);

  useEffect(() => {
    fetchUsuarios();
    fetchPerfis();
    fetchSetores();
  }, [fetchUsuarios, fetchPerfis, fetchSetores]);

  // Limpar feedback apos 5 segundos
  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => setFeedback({ type: '', message: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Filtro local por nome/username
  const filteredUsuarios = usuarios.filter((u) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (u.nome && u.nome.toLowerCase().includes(term)) ||
      (u.username && u.username.toLowerCase().includes(term))
    );
  });

  // formatDate importado de ../utils/formatDate

  // ---- Modal de Criacao/Edicao ----

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      nome: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      perfil_id: '',
      ativo: true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      nome: usuario.nome || '',
      username: usuario.username || '',
      email: usuario.email || '',
      password: '',
      confirmPassword: '',
      perfil_id: usuario.perfil_id || '',
      ativo: usuario.ativo !== undefined ? Boolean(usuario.ativo) : true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData({
      nome: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      perfil_id: '',
      ativo: true,
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nome.trim()) errors.nome = 'Nome e obrigatorio.';
    if (!editingUser) {
      if (!formData.username.trim()) errors.username = 'Username e obrigatorio.';
      if (!formData.password) errors.password = 'Senha e obrigatoria.';
      if (formData.password && formData.password.length < 4) errors.password = 'Senha deve ter pelo menos 4 caracteres.';
      if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'As senhas nao coincidem.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;
    setSavingForm(true);
    try {
      if (editingUser) {
        // Atualizar
        await axios.put(`${API_URL}/auth/users/${editingUser.id}`, {
          nome: formData.nome,
          email: formData.email,
          role: formData.perfil_id ? undefined : undefined, // role derived from perfil
          perfil_id: formData.perfil_id || null,
          ativo: formData.ativo ? 1 : 0,
        });
        setFeedback({ type: 'success', message: `Usuario "${formData.nome}" atualizado com sucesso.` });
      } else {
        // Criar
        await axios.post(`${API_URL}/auth/users`, {
          username: formData.username,
          password: formData.password,
          nome: formData.nome,
          email: formData.email,
          perfil_id: formData.perfil_id || null,
        });
        setFeedback({ type: 'success', message: `Usuario "${formData.nome}" criado com sucesso.` });
      }
      closeModal();
      fetchUsuarios();
    } catch (err) {
      console.error('Erro ao salvar usuario:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar usuario.';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSavingForm(false);
    }
  };

  // ---- Delete ----

  const handleDeleteUser = async (usuario) => {
    if (user && user.id === usuario.id) {
      setFeedback({ type: 'error', message: 'Voce nao pode deletar o proprio usuario.' });
      return;
    }
    const confirmed = window.confirm(
      `Tem certeza que deseja deletar o usuario "${usuario.nome || usuario.username}"? Esta acao nao pode ser desfeita.`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/auth/users/${usuario.id}`);
      setFeedback({ type: 'success', message: `Usuario "${usuario.nome || usuario.username}" deletado com sucesso.` });
      fetchUsuarios();
    } catch (err) {
      console.error('Erro ao deletar usuario:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao deletar usuario.';
      setFeedback({ type: 'error', message: msg });
    }
  };

  // ---- Modal de Reset de Senha ----

  const openResetModal = (usuario) => {
    setResetUserId(usuario.id);
    setResetUserName(usuario.nome || usuario.username);
    setResetData({ password: '', confirmPassword: '' });
    setResetErrors({});
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setResetUserId(null);
    setResetUserName('');
    setResetData({ password: '', confirmPassword: '' });
    setResetErrors({});
  };

  const validateReset = () => {
    const errors = {};
    if (!resetData.password) errors.password = 'Nova senha e obrigatoria.';
    if (resetData.password && resetData.password.length < 4) errors.password = 'Senha deve ter pelo menos 4 caracteres.';
    if (resetData.password !== resetData.confirmPassword) errors.confirmPassword = 'As senhas nao coincidem.';
    setResetErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validateReset()) return;
    setSavingReset(true);
    try {
      await axios.put(`${API_URL}/auth/users/${resetUserId}/reset-password`, {
        password: resetData.password,
      });
      setFeedback({ type: 'success', message: `Senha de "${resetUserName}" resetada com sucesso.` });
      closeResetModal();
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao resetar senha.';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSavingReset(false);
    }
  };

  // ---- Setores WhatsApp ----

  const handleSaveSetor = async () => {
    if (!setorForm.nome.trim()) return;
    setSavingSetor(true);
    try {
      if (editingSetor) {
        await axios.put(`${API_URL}/whatsapp/setores/${editingSetor.id}`, {
          nome: setorForm.nome,
          descricao: setorForm.descricao,
          ativo: editingSetor.ativo !== false,
        });
      } else {
        await axios.post(`${API_URL}/whatsapp/setores`, {
          nome: setorForm.nome,
          descricao: setorForm.descricao,
        });
      }
      setSetorModalOpen(false);
      setSetorForm({ nome: '', descricao: '' });
      setEditingSetor(null);
      fetchSetores();
      setFeedback({ type: 'success', message: editingSetor ? 'Setor atualizado!' : 'Setor criado!' });
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.error || 'Erro ao salvar setor' });
    } finally {
      setSavingSetor(false);
    }
  };

  const fetchSetorUsuarios = async (setorId) => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/setores/${setorId}/usuarios`);
      setSetorUsuarios((prev) => ({ ...prev, [setorId]: res.data }));
    } catch (err) {
      console.error('Erro ao buscar usuarios do setor:', err);
    }
  };

  const handleToggleSetor = (setorId) => {
    if (expandedSetor === setorId) {
      setExpandedSetor(null);
    } else {
      setExpandedSetor(setorId);
      fetchSetorUsuarios(setorId);
    }
  };

  const handleAddUserToSetor = async (setorId) => {
    if (!selectedUserForSetor) return;
    try {
      await axios.post(`${API_URL}/whatsapp/setores/${setorId}/usuarios`, {
        usuario_id: parseInt(selectedUserForSetor),
      });
      fetchSetorUsuarios(setorId);
      setSelectedUserForSetor('');
      setAddingUserToSetor(null);
      setFeedback({ type: 'success', message: 'Usuario adicionado ao setor!' });
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.error || 'Erro ao adicionar usuario' });
    }
  };

  const handleRemoveUserFromSetor = async (setorId, userId) => {
    if (!window.confirm('Remover este usuario do setor?')) return;
    try {
      await axios.delete(`${API_URL}/whatsapp/setores/${setorId}/usuarios/${userId}`);
      fetchSetorUsuarios(setorId);
      setFeedback({ type: 'success', message: 'Usuario removido do setor!' });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Erro ao remover usuario do setor' });
    }
  };

  return (
    <div className="data-page" style={styles.page}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink}>Inicio</span>
        <span style={{ margin: '0 0.4rem', color: '#999' }}>/</span>
        <span>Gerenciar Usuarios</span>
      </div>

      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>👤</span>
          <div>
            <h1 style={styles.pageTitle}>Gerenciar Usuarios</h1>
            <p style={styles.pageSubtitle}>Gerenciamento de usuarios do sistema</p>
          </div>
        </div>
        <button
          style={styles.btnPrimary}
          onClick={openCreateModal}
          onMouseEnter={(e) => (e.target.style.background = '#1565c0')}
          onMouseLeave={(e) => (e.target.style.background = '#1976d2')}
        >
          + Novo Usuario
        </button>
      </div>

      {/* Feedback */}
      {feedback.message && (
        <div style={styles.alert(feedback.type)}>
          {feedback.message}
        </div>
      )}

      {/* Toolbar: busca */}
      <div className="responsive-flex" style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por nome ou username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Tabela */}
      <div style={styles.tableCard}>
        <div style={styles.tableResponsive}>
          {loading ? (
            <div style={styles.loadingContainer}>Carregando usuarios...</div>
          ) : filteredUsuarios.length === 0 ? (
            <div style={styles.emptyState}>
              {searchTerm
                ? 'Nenhum usuario encontrado para o filtro aplicado.'
                : 'Nenhum usuario cadastrado.'}
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>Username</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Perfil</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
                  <th style={styles.th}>Ultimo Login</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usuario, index) => (
                  <tr
                    key={usuario.id}
                    style={index % 2 === 0 ? styles.trOdd : styles.trEven}
                  >
                    <td style={styles.td}>
                      <span style={{ fontWeight: '600', color: '#333' }}>{usuario.nome || '-'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: '#555', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {usuario.username}
                      </span>
                    </td>
                    <td style={styles.td}>{usuario.email || '-'}</td>
                    <td style={styles.td}>{usuario.perfil_nome || usuario.role || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={usuario.ativo ? styles.badgeAtivo : styles.badgeInativo}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDateTime(usuario.last_login)}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={styles.actionsCell}>
                        <button
                          style={styles.btnEdit}
                          title="Editar usuario"
                          onClick={() => openEditModal(usuario)}
                          onMouseEnter={(e) => (e.target.style.background = '#1565c0')}
                          onMouseLeave={(e) => (e.target.style.background = '#1976d2')}
                        >
                          Editar
                        </button>
                        <button
                          style={styles.btnWarning}
                          title="Resetar senha"
                          onClick={() => openResetModal(usuario)}
                          onMouseEnter={(e) => (e.target.style.background = '#f57c00')}
                          onMouseLeave={(e) => (e.target.style.background = '#ff9800')}
                        >
                          Senha
                        </button>
                        <button
                          style={{
                            ...styles.btnDanger,
                            ...(user && user.id === usuario.id
                              ? { opacity: 0.4, cursor: 'not-allowed' }
                              : {}),
                          }}
                          title={
                            user && user.id === usuario.id
                              ? 'Nao e possivel deletar o proprio usuario'
                              : 'Deletar usuario'
                          }
                          onClick={() => handleDeleteUser(usuario)}
                          disabled={user && user.id === usuario.id}
                          onMouseEnter={(e) => {
                            if (!(user && user.id === usuario.id)) e.target.style.background = '#c82333';
                          }}
                          onMouseLeave={(e) => {
                            if (!(user && user.id === usuario.id)) e.target.style.background = '#dc3545';
                          }}
                        >
                          Deletar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Contagem */}
      {!loading && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#999' }}>
          Exibindo {filteredUsuarios.length} de {usuarios.length} usuario(s)
        </div>
      )}

      {/* Separador */}
      <div style={{ borderTop: '2px solid #e0e0e0', margin: '2rem 0 1.5rem 0' }} />

      {/* Setores de Atendimento WhatsApp */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#333', margin: 0 }}>
          Setores de Atendimento WhatsApp
        </h2>
        <button
          style={styles.btnPrimary}
          onClick={() => {
            setEditingSetor(null);
            setSetorForm({ nome: '', descricao: '' });
            setSetorModalOpen(true);
          }}
          onMouseEnter={(e) => (e.target.style.background = '#1565c0')}
          onMouseLeave={(e) => (e.target.style.background = '#1976d2')}
        >
          + Novo Setor
        </button>
      </div>

      {loadingSetores ? (
        <div style={styles.loadingContainer}>Carregando setores...</div>
      ) : setores.length === 0 ? (
        <div style={styles.emptyState}>Nenhum setor cadastrado.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {setores.map((setor) => (
            <div
              key={setor.id}
              style={{
                background: '#fff',
                border: '1px solid #eee',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
              }}
            >
              {/* Setor header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  cursor: 'pointer',
                  gap: '0.75rem',
                }}
                onClick={() => handleToggleSetor(setor.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '0.85rem', color: '#999', fontWeight: '600', flexShrink: 0 }}>
                    {expandedSetor === setor.id ? '\u25BC' : '\u25B6'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#333' }}>
                      {setor.nome}
                    </span>
                    {setor.descricao && (
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#888', marginTop: '0.15rem' }}>
                        {setor.descricao}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    background: setor.ativo !== false ? '#e8f5e9' : '#ffebee',
                    color: setor.ativo !== false ? '#2e7d32' : '#c62828',
                  }}>
                    {setor.ativo !== false ? 'Ativo' : 'Inativo'}
                  </span>
                  <button
                    style={styles.btnEdit}
                    title="Editar setor"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSetor(setor);
                      setSetorForm({ nome: setor.nome || '', descricao: setor.descricao || '' });
                      setSetorModalOpen(true);
                    }}
                    onMouseEnter={(e) => (e.target.style.background = '#1565c0')}
                    onMouseLeave={(e) => (e.target.style.background = '#1976d2')}
                  >
                    Editar
                  </button>
                </div>
              </div>

              {/* Setor expanded content - usuarios */}
              {expandedSetor === setor.id && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '0.875rem 1rem', background: '#fafafa' }}>
                  {!setorUsuarios[setor.id] ? (
                    <div style={{ fontSize: '0.85rem', color: '#999', padding: '0.5rem 0' }}>Carregando usuarios...</div>
                  ) : setorUsuarios[setor.id].length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#999', padding: '0.5rem 0' }}>Nenhum usuario neste setor.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
                      {setorUsuarios[setor.id].map((su) => (
                        <div
                          key={su.usuario_id || su.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.5rem 0.75rem',
                            background: '#fff',
                            borderRadius: '6px',
                            border: '1px solid #eee',
                            fontSize: '0.875rem',
                          }}
                        >
                          <span style={{ color: '#333', fontWeight: '500' }}>{su.nome || su.username || `ID ${su.usuario_id}`}</span>
                          <button
                            style={{
                              background: 'none',
                              border: '1px solid #e57373',
                              color: '#c62828',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                            }}
                            title="Remover usuario do setor"
                            onClick={() => handleRemoveUserFromSetor(setor.id, su.usuario_id || su.id)}
                            onMouseEnter={(e) => { e.target.style.background = '#ffebee'; }}
                            onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add user to setor */}
                  {addingUserToSetor === setor.id ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        style={{ ...styles.select, width: 'auto', minWidth: '200px', flex: 1 }}
                        value={selectedUserForSetor}
                        onChange={(e) => setSelectedUserForSetor(e.target.value)}
                      >
                        <option value="">Selecionar usuario</option>
                        {usuarios
                          .filter((u) => u.ativo && !(setorUsuarios[setor.id] || []).some((su) => (su.usuario_id || su.id) === u.id))
                          .map((u) => (
                            <option key={u.id} value={u.id}>{u.nome || u.username}</option>
                          ))}
                      </select>
                      <button
                        style={{
                          ...styles.btnPrimary,
                          padding: '0.4rem 0.75rem',
                          fontSize: '0.8rem',
                        }}
                        onClick={() => handleAddUserToSetor(setor.id)}
                        onMouseEnter={(e) => (e.target.style.background = '#1565c0')}
                        onMouseLeave={(e) => (e.target.style.background = '#1976d2')}
                      >
                        Adicionar
                      </button>
                      <button
                        style={{
                          ...styles.btnSecondary,
                          padding: '0.4rem 0.75rem',
                          fontSize: '0.8rem',
                        }}
                        onClick={() => {
                          setAddingUserToSetor(null);
                          setSelectedUserForSetor('');
                        }}
                        onMouseEnter={(e) => (e.target.style.background = '#5a6268')}
                        onMouseLeave={(e) => (e.target.style.background = '#6c757d')}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      style={{
                        background: 'none',
                        border: '1px dashed #bbb',
                        color: '#666',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, color 0.2s',
                        width: '100%',
                      }}
                      onClick={() => {
                        setAddingUserToSetor(setor.id);
                        setSelectedUserForSetor('');
                      }}
                      onMouseEnter={(e) => { e.target.style.borderColor = '#1976d2'; e.target.style.color = '#1976d2'; }}
                      onMouseLeave={(e) => { e.target.style.borderColor = '#bbb'; e.target.style.color = '#666'; }}
                    >
                      + Adicionar usuario
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de Setor (Criar/Editar) */}
      {setorModalOpen && (
        <div style={styles.overlay} onClick={() => { setSetorModalOpen(false); setEditingSetor(null); setSetorForm({ nome: '', descricao: '' }); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingSetor ? 'Editar Setor' : 'Novo Setor'}
              </h2>
              <button style={styles.modalCloseBtn} onClick={() => { setSetorModalOpen(false); setEditingSetor(null); setSetorForm({ nome: '', descricao: '' }); }}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Nome <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(!setorForm.nome.trim() && savingSetor ? { borderColor: '#dc3545' } : {}),
                  }}
                  value={setorForm.nome}
                  onChange={(e) => setSetorForm({ ...setorForm, nome: e.target.value })}
                  placeholder="Nome do setor"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Descricao</label>
                <input
                  type="text"
                  style={styles.input}
                  value={setorForm.descricao}
                  onChange={(e) => setSetorForm({ ...setorForm, descricao: e.target.value })}
                  placeholder="Descricao do setor (opcional)"
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={() => { setSetorModalOpen(false); setEditingSetor(null); setSetorForm({ nome: '', descricao: '' }); }}
                onMouseEnter={(e) => (e.target.style.background = '#5a6268')}
                onMouseLeave={(e) => (e.target.style.background = '#6c757d')}
              >
                Cancelar
              </button>
              <button
                style={{
                  ...styles.btnPrimary,
                  ...(savingSetor ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleSaveSetor}
                disabled={savingSetor}
                onMouseEnter={(e) => { if (!savingSetor) e.target.style.background = '#1565c0'; }}
                onMouseLeave={(e) => { if (!savingSetor) e.target.style.background = '#1976d2'; }}
              >
                {savingSetor ? 'Salvando...' : editingSetor ? 'Salvar Alteracoes' : 'Criar Setor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criacao/Edicao */}
      {modalOpen && (
        <div style={styles.overlay} onClick={closeModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingUser ? 'Editar Usuario' : 'Novo Usuario'}
              </h2>
              <button style={styles.modalCloseBtn} onClick={closeModal}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              {/* Nome */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Nome completo <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(formErrors.nome ? { borderColor: '#dc3545' } : {}),
                  }}
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome do usuario"
                />
                {formErrors.nome && (
                  <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                    {formErrors.nome}
                  </span>
                )}
              </div>

              {/* Username (somente na criacao) */}
              {!editingUser && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Username <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    style={{
                      ...styles.input,
                      ...(formErrors.username ? { borderColor: '#dc3545' } : {}),
                    }}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Login do usuario"
                    autoComplete="off"
                  />
                  {formErrors.username && (
                    <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                      {formErrors.username}
                    </span>
                  )}
                </div>
              )}

              {/* Email */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  style={styles.input}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>

              {/* Senha (somente na criacao) */}
              {!editingUser && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Senha <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="password"
                      style={{
                        ...styles.input,
                        ...(formErrors.password ? { borderColor: '#dc3545' } : {}),
                      }}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Senha do usuario"
                      autoComplete="new-password"
                    />
                    {formErrors.password && (
                      <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                        {formErrors.password}
                      </span>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Confirmar senha <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="password"
                      style={{
                        ...styles.input,
                        ...(formErrors.confirmPassword ? { borderColor: '#dc3545' } : {}),
                      }}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Repita a senha"
                      autoComplete="new-password"
                    />
                    {formErrors.confirmPassword && (
                      <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                        {formErrors.confirmPassword}
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Perfil */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Perfil</label>
                <select
                  style={styles.select}
                  value={formData.perfil_id}
                  onChange={(e) => setFormData({ ...formData, perfil_id: e.target.value })}
                >
                  <option value="">
                    {perfis.length === 0 ? 'Nenhum perfil cadastrado' : 'Selecione um perfil'}
                  </option>
                  {perfis.map((perfil) => (
                    <option key={perfil.id} value={perfil.id}>
                      {perfil.nome}
                    </option>
                  ))}
                </select>
                {perfis.length === 0 && (
                  <small style={{ color: '#c62828', display: 'block', marginTop: '6px' }}>
                    Nenhum perfil cadastrado. Crie um em Sistema {'>'} Gerenciar Perfis para poder
                    atribuir aqui.
                  </small>
                )}
              </div>

              {/* Toggle Ativo/Inativo */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={styles.toggleContainer}>
                  <div
                    style={styles.toggleSwitch}
                    onClick={() => setFormData({ ...formData, ativo: !formData.ativo })}
                  >
                    <div style={styles.toggleTrack(formData.ativo)} />
                    <div style={styles.toggleKnob(formData.ativo)} />
                  </div>
                  <span style={{ fontSize: '0.9rem', color: formData.ativo ? '#2e7d32' : '#c62828', fontWeight: '600' }}>
                    {formData.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={closeModal}
                onMouseEnter={(e) => (e.target.style.background = '#5a6268')}
                onMouseLeave={(e) => (e.target.style.background = '#6c757d')}
              >
                Cancelar
              </button>
              <button
                style={{
                  ...styles.btnPrimary,
                  ...(savingForm ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleSaveUser}
                disabled={savingForm}
                onMouseEnter={(e) => {
                  if (!savingForm) e.target.style.background = '#1565c0';
                }}
                onMouseLeave={(e) => {
                  if (!savingForm) e.target.style.background = '#1976d2';
                }}
              >
                {savingForm ? 'Salvando...' : editingUser ? 'Salvar Alteracoes' : 'Criar Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {resetModalOpen && (
        <div style={styles.overlay} onClick={closeResetModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Resetar Senha</h2>
              <button style={styles.modalCloseBtn} onClick={closeResetModal}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#666' }}>
                Definir nova senha para o usuario <strong>{resetUserName}</strong>.
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Nova senha <span style={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  style={{
                    ...styles.input,
                    ...(resetErrors.password ? { borderColor: '#dc3545' } : {}),
                  }}
                  value={resetData.password}
                  onChange={(e) => setResetData({ ...resetData, password: e.target.value })}
                  placeholder="Nova senha"
                  autoComplete="new-password"
                />
                {resetErrors.password && (
                  <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                    {resetErrors.password}
                  </span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Confirmar nova senha <span style={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  style={{
                    ...styles.input,
                    ...(resetErrors.confirmPassword ? { borderColor: '#dc3545' } : {}),
                  }}
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  placeholder="Repita a nova senha"
                  autoComplete="new-password"
                />
                {resetErrors.confirmPassword && (
                  <span style={{ fontSize: '0.8rem', color: '#dc3545', marginTop: '0.2rem', display: 'block' }}>
                    {resetErrors.confirmPassword}
                  </span>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={closeResetModal}
                onMouseEnter={(e) => (e.target.style.background = '#5a6268')}
                onMouseLeave={(e) => (e.target.style.background = '#6c757d')}
              >
                Cancelar
              </button>
              <button
                style={{
                  ...styles.btnWarning,
                  padding: '0.625rem 1.25rem',
                  fontSize: '0.9rem',
                  ...(savingReset ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleResetPassword}
                disabled={savingReset}
                onMouseEnter={(e) => {
                  if (!savingReset) e.target.style.background = '#f57c00';
                }}
                onMouseLeave={(e) => {
                  if (!savingReset) e.target.style.background = '#ff9800';
                }}
              >
                {savingReset ? 'Salvando...' : 'Confirmar Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarUsuarios;
